import type { ReactTable, RowData } from "@tanstack/react-table";
import {
  columnFilteringFeature,
  columnOrderingFeature,
  columnPinningFeature,
  columnResizingFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  createFilteredRowModel,
  createPaginatedRowModel,
  createSortedRowModel,
  globalFilteringFeature,
  metaHelper,
  rowPaginationFeature,
  rowSelectionFeature,
  rowSortingFeature,
  sortFn_alphanumeric,
  sortFn_datetime,
  sortFn_text,
  tableFeatures,
} from "@tanstack/react-table";

export type DataTableColumnMeta = {
  flex?: number;
  align?: "left" | "center" | "right";
  draggable?: boolean;
  compact?: boolean;
  frozen?: boolean;
  /** Structural columns (e.g. selection checkbox, inbox icon) that are always pinned-left and never user-managed. */
  alwaysPinned?: boolean;
  /**
   * Floor applied to an unresized column's width on first load so its header stays fully visible. The (smaller)
   * `minSize` is the resize floor, letting the user drag the column down past its header to just its icons.
   */
  initialMinWidth?: number;
};

/**
 * The TanStack Table feature set every `DataTable` grid registers.
 *
 * v9 ships no features by default, and `DataTable` calls into all of these regardless of which table is rendering —
 * sorting and pagination on the header row and footer, pinning and sizing in the sticky-offset layout, ordering for
 * drag-to-reorder, visibility for the hide-column menu, selection for the checkbox column. So they are registered
 * once here rather than per call site, and a table that doesn't use one simply leaves its state at the default.
 *
 * `sortFns` carries the three functions v9's `auto` sort resolution can name (`datetime`, `alphanumeric`, `text`):
 * unregistered, auto-resolution silently falls back to `sortFn_basic` and text columns stop sorting the way they did
 * on v8. Filter functions need no such registration — an unset `globalFilterFn` defaults to `auto`, which v9 resolves
 * to `includesString` directly.
 *
 * Because `paginatedRowModel` is registered here, `table.getRowModel()` pages every table through it. A table that
 * renders all of its rows must say so with `manualPagination: true`, or it silently shows only the first ten.
 */
export const dataTableFeatures = tableFeatures({
  columnFilteringFeature,
  globalFilteringFeature,
  columnOrderingFeature,
  columnPinningFeature,
  columnSizingFeature,
  columnResizingFeature,
  columnVisibilityFeature,
  rowPaginationFeature,
  rowSelectionFeature,
  rowSortingFeature,
  filteredRowModel: createFilteredRowModel(),
  sortedRowModel: createSortedRowModel(),
  paginatedRowModel: createPaginatedRowModel(),
  sortFns: { alphanumeric: sortFn_alphanumeric, datetime: sortFn_datetime, text: sortFn_text },
  columnMeta: metaHelper<DataTableColumnMeta>(),
});

export type DataTableFeatures = typeof dataTableFeatures;

/** A table instance built from {@link dataTableFeatures} — what `useTable` returns and `DataTable` renders. */
export type DataTableInstance<T extends RowData> = ReactTable<DataTableFeatures, T>;
